import { useCheckInAppointment } from '@/api/hooks/useCheckInAppointment'
import { useConfirmPatientAppointment } from '@/api/hooks/useConfirmPatientAppointment'
import type { UpcomingVisit } from '@/api/types/ProviderApi'
import { visitTitle } from '@/components/HomePage/HomeTiles/VisitCard'
import { CantMakeIt } from '@/components/VisitPage/CantMakeIt'
import { CardRule } from '@/components/VisitPage/CardRule'
import { Glyph } from '@/components/svgs/Glyph'
import { colors } from '@/constants/colors'
import { addVisitToCalendar } from '@/features/scheduling/calendar'
import { shouldShowCheckIn } from '@/features/visits/checkIn'
import { visitSlotState } from '@/features/visits/slot'
import { analytics } from '@/lib/analytics'
import { Button, ButtonText } from '@/ui/core/button'
import { useCardGround, useDiscColor } from '@/ui/core/card-ground'
import { HStack, VStack } from '@/ui/core/stack'
import { Text } from '@/ui/core/typography'
import { router } from 'expo-router'
import { useState } from 'react'
import { View } from 'react-native'
import Toast from 'react-native-toast-message'

/**
 * What a button looks like when it cannot be pressed yet, on each of the two grounds.
 *
 * On white it is the library's own inactive recipe, unaltered: the surface grey as a fill, the
 * line grey as a ring, the muted label the button styles already give a disabled control. That
 * recipe was rejected once — on blush, where `surface` is *lighter* than the ground and made the
 * one thing nobody can press the brightest object in the card. It works here because the card
 * finally is white, which is the ground it was drawn for.
 *
 * On blush it stays white-bodied, which is the one fill that separates from that ground, and the
 * ring drops to `edge` — the outline this app puts round an input. Solid grey at full strength
 * drew a dormant button harder than the live sentence under it.
 */
const INACTIVE_WHITE = {
	backgroundColor: colors.surface,
	borderColor: colors.line,
} as const
const INACTIVE_BLUSH = {
	backgroundColor: colors.white,
	borderColor: colors.edge,
} as const

/**
 * Shorter than the app's `lg`, which is the size a form's submit takes at the foot of a screen.
 *
 * Fifty-two points full-width made the slot the heaviest thing on the card after the photograph,
 * which is fair while the button is live and wrong for the weeks it is dark. Forty-six is still
 * two points clear of the smallest thing a thumb should be asked to hit, and sits under the
 * weight of the rows above it — which are sixty-four and sixty-eight.
 *
 * One height in every state: the card's shape must not change when the button wakes up.
 */
const SLOT_HEIGHT = { minHeight: 46 } as const

/**
 * The foot of a visit with nothing left to do: the one useful thing, and the way out.
 *
 * A confirmed visit three weeks out had a bare link under four rows, which is one of the four
 * placements `CantMakeIt` was tried in and rejected — a link floating with nothing around it
 * reads as a page that stopped early. Adding it to a calendar is the real thing a member wants
 * from a settled visit, and the booked screen already offers it from the same helper, so anybody
 * who skipped it there gets it back here.
 *
 * Outlined, not filled. It is optional housekeeping, and filling it would make the loudest thing
 * on the page an errand about a visit that is already settled.
 *
 * On Home's card as well as the page. The card used to keep the dark "I'm Here" here, on the
 * argument that a card whose last row is a link has lost its foot — but a settled visit three
 * weeks out has no door to name, so that button said nothing at all, and Add to Calendar is a
 * foot. Where the payload carries no check-in block the label had nothing it could ever say.
 */
const SettledFoot = ({ visit, bare }: { visit: UpcomingVisit; bare: boolean }) => {
	const [saving, setSaving] = useState(false)

	const onAdd = async () => {
		analytics.trackClick('visit-add-to-calendar', { appointmentId: visit.appointmentId })
		setSaving(true)
		const result = await addVisitToCalendar({ ...visit, name: visitTitle(visit) })
		setSaving(false)
		Toast.show(
			result === 'added'
				? { type: 'success', text1: 'Added to your calendar' }
				: {
						type: 'error',
						text1:
							result === 'denied'
								? 'Wally needs calendar access to add it'
								: "We couldn't add it to your calendar",
				  }
		)
	}

	return (
		<VStack
			gap={10}
			className={bare ? 'px-0' : 'px-0 pt-3.5'}
			style={bare ? undefined : { paddingBottom: 8 }}
		>
			<Button
				variant="secondary"
				size="lg"
				fullWidth
				isLoading={saving}
				disabled={saving}
				onPress={onAdd}
				style={SLOT_HEIGHT}
			>
				<ButtonText>Add to Calendar</ButtonText>
			</Button>
			<CantMakeIt visit={visit} />
		</VStack>
	)
}

/**
 * What is left once check-in has closed: the chat, and an apology.
 *
 * Outlined rather than filled, because the front desk is still the faster answer and the app
 * should not dress the slower route as the primary one. The line under it carries the apology and
 * the reason to press, in the place the reschedule link used to sit.
 */
const ClosedFoot = ({ visit, bare }: { visit: UpcomingVisit; bare: boolean }) => (
	<VStack
		gap={10}
		className={bare ? 'px-0' : 'px-0 pt-3.5'}
		style={bare ? undefined : { paddingBottom: 8 }}
	>
		<Button
			variant="secondary"
			size="lg"
			fullWidth
			onPress={() => {
				analytics.trackClick('visit-check-in-closed-chat', {
					appointmentId: visit.appointmentId,
				})
				router.navigate('/chat')
			}}
			style={SLOT_HEIGHT}
		>
			<ButtonText>Chat With Us</ButtonText>
		</Button>
		<Text size="body2" tone="soft" className="text-center">
			Sorry we missed you. Message us if you need help.
		</Text>
	</VStack>
)

/**
 * The card's foot: one thing to do, or the receipt for having done it.
 *
 * A slot rather than a stack of panels. The page used to grow a block on the day and another one
 * on arrival, so the thing a member came for moved down the screen as the visit got closer; here
 * the card's shape is fixed and only the contents of its last row change. `visitSlotState` owns
 * which of them it is — this file owns what each looks like and what it calls.
 *
 * One button, full width, and nothing beside it. Move It sat here as an outlined button next to a
 * filled one, and at a glance the two read as a pair of equal choices — when one is the thing to
 * do and the other is the way out of doing it. The ways out are a group of their own further down
 * the page, under the question a member who wants them is actually asking.
 */
export const VisitSlot = ({
	visit,
	bare = false,
}: {
	visit: UpcomingVisit
	/**
	 * Standing on the page rather than inside a card: no side padding, because the page's own
	 * column is the inset and a full-width action that stops short of it reads as a list item.
	 *
	 * Its own prop rather than read off `useCardGround`, which is what decided this while the
	 * card and the page were one component. The ground answers what colour a disc is; where a
	 * button's edges are is a question about layout, and a page has no ground at all.
	 */
	bare?: boolean
}) => {
	const { mutate: confirmAppointment, isPending: isConfirming } =
		useConfirmPatientAppointment()
	const { mutate: checkIn, isPending: isArriving } = useCheckInAppointment()
	const state = visitSlotState(visit)
	const onWhite = bare || useCardGround() === 'white'
	const disc = useDiscColor()

	const onArrive = () => {
		analytics.trackClick('check-in-appointment', { appointmentId: visit.appointmentId })
		checkIn(
			{ appointmentId: visit.appointmentId },
			{
				// The server answers with the state whatever it is, so someone who was already in,
				// or a second too late, is told the truth rather than shown a failure.
				onSuccess: (next) => {
					if (next.status === 'CHECKED_IN') {
						Toast.show({ type: 'success', text1: "We know you're here" })
					}
				},
				onError: () =>
					Toast.show({
						type: 'error',
						text1: 'Could not let them know. Please try again.',
					}),
			}
		)
	}

	const onConfirm = () => {
		analytics.trackClick('confirm-appointment', {
			appointmentId: visit.appointmentId,
			appointmentTypes: visit.types.map((type) => type.value).join(','),
		})
		confirmAppointment(
			{ appointmentId: visit.appointmentId },
			{
				onSuccess: () =>
					Toast.show({ type: 'success', text1: 'Your visit is confirmed' }),
				onError: () =>
					Toast.show({
						type: 'error',
						text1: 'Could not confirm your visit, please try again',
					}),
			}
		)
	}

	// The page draws its own "You're In" row, from the same state. Inside the card this branch is
	// the only thing that says a member is through the door.
	if (state.kind === 'arrived' && bare) {
		return null
	}

	// The window has shut. Not a dead "I'm Here": a way to reach somebody, and the sentence that
	// says why. No reschedule link here — the person on the other end of the chat can move a
	// visit, and a picker is the wrong thing to offer to somebody already in the building.
	if (state.kind === 'closed') {
		return <ClosedFoot visit={visit} bare={bare} />
	}

	if (state.kind === 'arrived') {
		/*
		 * The state, as a row — because every line below the photograph in this card is a row.
		 *
		 * It was a mint band, which is the loudest thing on the card at the one moment nothing is
		 * being asked of anybody, and which said a third time what the chip in the corner and the
		 * headline on the photograph have already said twice. Same disc, same title-and-line, same
		 * hairline as the address and the guide above it; the only difference is the green mark,
		 * and the fact that the card is shorter than it was a minute ago — the pass row goes when
		 * a member is through the door, and the button goes with it.
		 */
		return (
			<HStack gap={12} className="items-center px-4 py-4">
				<CardRule />
				<View
					className="h-10 w-10 items-center justify-center rounded-full"
					style={{ backgroundColor: disc }}
				>
					{/* Black, like every other mark in this card. The green was the one spend of
					    colour on the screen at the one moment nothing is being asked of anybody —
					    a mark that shouts about a state the words beside it already carry. */}
					<Glyph name="check" size={19} color={colors.ink} />
				</View>
				<VStack gap={2} className="flex-1">
					{/* A person's words, not a record's: "checked in" is what a system says about a
					    row in a table. This is what the receptionist would have said. */}
					<Text size="body0" weight="bold">
						You're In
					</Text>
					<Text size="body2" tone="muted">
						We'll come and get you shortly
					</Text>
				</VStack>
			</HStack>
		)
	}

	const isArrival = state.kind === 'arrive'
	const label = state.label

	/*
	 * A door that is weeks away is not a button.
	 *
	 * Once a visit is confirmed the slot moves on to arriving, which is right on the morning and
	 * absurd three weeks out: a dead "I'm Here" under a visit in October is a control that cannot
	 * be used for a month, and on the page the row above it has already said the visit is settled.
	 * So the page drops the button and keeps the way out; the card keeps it, because a card whose
	 * last row is a link has lost its foot.
	 *
	 * `shouldShowCheckIn` is the hour's margin this was given when the check-in block landed and
	 * never wired to anything.
	 */
	const doorFarOff =
		isArrival &&
		!state.enabled &&
		(!visit.checkIn || !shouldShowCheckIn(visit.checkIn, new Date()))

	if (doorFarOff) {
		return <SettledFoot visit={visit} bare={bare} />
	}

	return (
		/* On white the button spans the card's mount, wider than the rows are inset: it is the
		   card's action rather than a row in the list, and a full-width control that stops short
		   of the card's own margin reads as an item. On blush it keeps the rows' padding, because
		   there it is inside their block. */
		/* Eight under the small print on white, on top of the card's own twelve of mount: twenty
		   from the line to the card's foot, which is the air whitening's card gives the same
		   sentence in the same place. The mount alone read as the line resting on the edge — it
		   is the last thing in the card and the only one with nothing below it to hold it up.
		   On blush the block closes itself with its own padding. */
		<VStack
			gap={10}
			className={bare ? 'px-0' : onWhite ? 'px-0 pt-3.5' : 'px-4 py-3.5'}
			style={!bare && onWhite ? { paddingBottom: 8 } : undefined}
		>
			<Button
				size="lg"
				fullWidth
				disabled={!state.enabled || isArriving || isConfirming}
				isLoading={isArrival ? isArriving : isConfirming}
				onPress={isArrival ? onArrive : onConfirm}
				style={
					state.enabled
						? SLOT_HEIGHT
						: { ...SLOT_HEIGHT, ...(onWhite ? INACTIVE_WHITE : INACTIVE_BLUSH) }
				}
			>
				{/* The library's muted label on white; a quieter one on blush, where a solid grey
				    word inside a white pill outdrew the sentence under it. */}
				<ButtonText className={!state.enabled && !onWhite ? 'text-edge' : undefined}>
					{label}
				</ButtonText>
			</Button>
			{/* The line under the button: why it is dark where it is, and the way out, in one
			    sentence. `CantMakeIt` sets both, because the two are one line. Not drawn once a
			    member is in the waiting room — that branch returns above, and nobody reschedules
			    a visit they are sitting in. */}
			{/* No sentence on the page: `StateRow` says it there, in a row, with the moment named
			    rather than counted. Inside the card this line is the only place it can be said. */}
			<CantMakeIt visit={visit} helper={bare ? undefined : state.helper} />
		</VStack>
	)
}
